import React, { useContext } from 'react';
import Carousel from 'components/sections/carousel/Carousel';
import SchoolCard from 'components/sections/lists/ClickPortalResults/result-card/schoolCard/SchoolCard';
import { CategoryGuid, MultiRowSchoolListingResults } from 'types';
import ClickPortalResultRowsLoader from 'components/sections/clickPortalResultRows/ClickPortalResultRowsLoader';
import { FIELD_NAMES, BREAKPOINT_MAP } from 'consts';
import GlobalContext from 'hooks/contexts/GlobalContext';
import getFormSelectionObjects from 'components/form-wizards/click-portal-search/utils/getFormSelectionObjects';
import get from 'lodash/get';

export interface ClickPortalResultRowsListProps {
  resultRow: MultiRowSchoolListingResults['resultRows'][number];
  scrollRef: React.RefObject<HTMLDivElement>;
  isLoading: boolean;
  wpTitle?: string;
  wpSubTitle?: string;
}

export default function ClickPortalResultRowsList({
  resultRow,
  scrollRef,
  isLoading,
  wpTitle,
  wpSubTitle,
}: ClickPortalResultRowsListProps): React.JSX.Element | null {
  const {
    programTaxonomyMap,
    windowSize: { currentBreakpoint },
  } = useContext(GlobalContext);

  const rowSelection = {
    [FIELD_NAMES.CLICK_PORTAL_DEGREES]:
      resultRow.filterKeyDerivedValues.degree_level_guid,
    [FIELD_NAMES.CLICK_PORTAL_SUBJECTS]:
      resultRow.filterKeyDerivedValues.parent_category_guid,
    [FIELD_NAMES.CLICK_PORTAL_CONCENTRATIONS]:
      resultRow.filterKeyDerivedValues.category_guid,
  };
  const { degree, category, subject } = getFormSelectionObjects(
    rowSelection,
    programTaxonomyMap
  );

  const hasSelection = category && degree && subject;

  function getTitle(): string {
    if (wpTitle) {
      return wpTitle;
    }

    if (resultRow?.title) {
      return resultRow?.title;
    }

    if (hasSelection) {
      return `Online ${get(category, 'label')} Degree Programs`;
    }
    return resultRow?.title;
  }

  function getSubTitle(): string {
    if (wpSubTitle) {
      return wpSubTitle;
    }

    if (resultRow?.subTitle) {
      return resultRow?.subTitle;
    }

    if (hasSelection) {
      return 'Most popular schools and programs';
    }
    return resultRow?.subTitle;
  }
  // If there are no results, don't render the list
  if (!isLoading && resultRow?.results?.length === 0) {
    return null;
  }

  const stepSize = currentBreakpoint === BREAKPOINT_MAP.MOBILE.label ? 1 : 2;

  return (
    <div className="clickPortalResultRowsSection__list" ref={scrollRef}>
      <ClickPortalResultRowsLoader isLoading={isLoading} />
      {!isLoading && (
        <>
          <div className="clickPortalResultRowsSection__listHeadings">
            <h3 className="clickPortalResultRowsSection__listTitle">
              {getTitle()}
            </h3>
            <p className="clickPortalResultRowsSection__listSubTitle">
              {getSubTitle()}
            </p>
          </div>

          <Carousel scrollToActiveSlide={false} stepSize={stepSize}>
            {resultRow?.results?.map((school, cardIndex) => {
              return (
                <SchoolCard
                  rowCategoryGuid={
                    (resultRow?.filterKeyDerivedValues
                      ?.category_guid as CategoryGuid) ?? null
                  }
                  index={cardIndex}
                  key={school.id}
                  schoolInfo={school}
                  isLoading={isLoading}
                  badge={cardIndex === 0 ? "Editor's Choice" : ''}
                />
              );
            })}
          </Carousel>
        </>
      )}
    </div>
  );
}
